import { createFileRoute } from '@tanstack/react-router'
import { OnboardingFlow } from '@workspace/seedforge-features/onboarding'
import { cn } from '@workspace/ui/lib/utils'
import { Briefcase, Globe, Loader2, MapPin, Sparkles, TrendingUp, Upload } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import {
  useCurrentUserQuery,
  useMyMembershipsQuery,
  usePublicInvestorQuery,
  useStartupConfigQuery,
  useTriggerPrescreenMutation,
} from '../hooks/api'
import { auth, useSession } from '../lib/auth'
import { createOnboardingApiFromSdk } from '../lib/onboarding-api'
import { signupQuestionSource, useReportSignupSource } from '../lib/signup-source'

export const Route = createFileRoute('/$slug')({
  component: InvestorPitchPage,
})

type Step = 'upload' | 'onboarding'

function InvestorPitchPage() {
  const { slug } = Route.useParams()
  const { data: investor, isLoading, isError } = usePublicInvestorQuery(slug)
  const { data: session } = useSession()
  useReportSignupSource(session?.user?.id, session?.user?.createdAt)
  const { data: currentUser } = useCurrentUserQuery({ enabled: !!session })

  const isAuthenticated = !!session
  const isInvestorUser = currentUser?.userType === 'INVESTOR'
  const isOnboarded = currentUser?.onboardingCompleted === true
  const { data: memberships } = useMyMembershipsQuery({ enabled: isAuthenticated && isOnboarded })
  const startupId = memberships?.find((m) => m.type === 'startup')?.id
  const { data: startupConfig } = useStartupConfigQuery({ enabled: isAuthenticated })

  const api = useMemo(() => createOnboardingApiFromSdk(), [])
  const triggerPrescreen = useTriggerPrescreenMutation(startupId ?? '')
  const [step, setStep] = useState<Step>('upload')
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Onboarded founder: upload + submit prescreen + redirect
  const handleSubmitToInvestor = useCallback(
    async (file: File) => {
      if (!startupId) return
      setSubmitting(true)
      setError(null)
      try {
        const doc = await api.createDocument(file.name)
        await api.uploadToPresignedUrl(doc.uploadUrl, doc.uploadFields, file)
        await api.completeDocumentUpload(doc.id)

        await triggerPrescreen.mutateAsync({
          path: { startupId, documentId: doc.id },
          body: { investorId: investor?.id },
        })

        window.location.href = `/app/startup/${startupId}/feed`
      } catch {
        setError('Failed to submit pitch deck. Please try again.')
        setSubmitting(false)
      }
    },
    [startupId, investor?.id, api, triggerPrescreen]
  )

  const handleFile = useCallback(
    (file: File) => {
      if (isOnboarded && startupId && !isInvestorUser) {
        handleSubmitToInvestor(file)
      } else {
        setPendingFile(file)
        setStep('onboarding')
      }
    },
    [isOnboarded, startupId, isInvestorUser, handleSubmitToInvestor]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files?.[0]
      if (file) handleFile(file)
    },
    [handleFile]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleFile(file)
    },
    [handleFile]
  )

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="size-5 animate-spin rounded-full border-2 border-[#f97316] border-t-transparent" />
      </div>
    )
  }

  if (isError || !investor) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-foreground">Not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This page doesn't exist or has been removed.
          </p>
        </div>
      </div>
    )
  }

  const investorLeftPanel = <InvestorLeftPanel investor={investor} />

  // After file selection: full onboarding flow with investor left panel
  if (step === 'onboarding' && pendingFile) {
    return (
      <OnboardingFlow
        api={api}
        sendOtp={async (e) => {
          await auth.emailOtp.sendVerificationOtp({ email: e, type: 'sign-in' })
        }}
        verifyOtp={async (e, otp) => {
          const res = await auth.signIn.emailOtp({ email: e, otp })
          return !res.error
        }}
        onComplete={(result) => {
          if (result.startupId) {
            window.location.href = `/app/startup/${result.startupId}`
          } else {
            window.location.href = '/app'
          }
        }}
        authenticated={isAuthenticated}
        userEmail={session?.user?.email}
        userName={session?.user?.name || undefined}
        userRole="founder"
        pendingPitchFile={pendingFile}
        investorId={investor?.id}
        leftPanel={investorLeftPanel}
        formTitle="Get feedback on your startup"
        formSubtitle="Continue with your email to get started"
        stageOptions={startupConfig?.productStages}
        raiseStageOptions={startupConfig?.raiseStages}
        categoryOptions={startupConfig?.categories?.map((c) => ({
          value: c.id,
          label: c.name,
        }))}
        signupQuestion={signupQuestionSource}
      />
    )
  }

  return (
    <div className="flex min-h-screen bg-background">
      {investorLeftPanel}

      {/* Right panel */}
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-6">
          <MobileInvestorHeader investor={investor} />

          {isAuthenticated && isInvestorUser ? (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-3 duration-400">
              <h1 className="text-center text-xl font-semibold text-foreground">
                Pitching is for founders
              </h1>
              <p className="text-center text-sm text-muted-foreground">
                You're signed in as an investor. To pitch to {investor.name}, please sign in with a
                founder account.
              </p>
            </div>
          ) : submitting ? (
            <div className="flex flex-col items-center gap-3 py-12 animate-in fade-in duration-300">
              <Loader2 className="size-8 animate-spin text-[#f97316]" />
              <p className="text-sm font-medium text-foreground">Submitting your pitch deck...</p>
              <p className="text-xs text-muted-foreground">This may take a moment</p>
            </div>
          ) : (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-400">
              {error && (
                <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-2.5 text-sm text-red-400">
                  {error}
                </div>
              )}

              <div>
                <h1 className="text-center text-xl font-semibold text-foreground">
                  Upload your pitch deck
                </h1>
                <p className="mt-2 text-center text-sm text-muted-foreground">
                  {investor.name} will receive your deck along with AI-powered feedback. Top pitches
                  get a chance to pitch live.
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={handleInputChange}
              />

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragOver(true)
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={cn(
                  'flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 transition-all',
                  dragOver
                    ? 'border-[#f97316] bg-[#f97316]/5'
                    : 'border-border hover:border-[#f97316]/40 hover:bg-[#f97316]/[0.02]'
                )}
              >
                <Upload className="size-6 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Drop your pitch deck here</p>
                <p className="text-xs text-muted-foreground">PDF, up to 50MB</p>
              </button>

              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-3">
                  <Sparkles className="mt-0.5 size-4 shrink-0 text-[#f97316]" />
                  <span className="text-muted-foreground">
                    Get AI feedback from multiple expert perspectives
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <TrendingUp className="mt-0.5 size-4 shrink-0 text-[#f97316]" />
                  <span className="text-muted-foreground">
                    Top-rated pitches get fast-tracked to the investor
                  </span>
                </li>
              </ul>
            </div>
          )}

          <p className="text-center text-xs text-muted-foreground/60 lg:hidden">
            Powered by <span className="font-medium text-muted-foreground/80">SeedForge</span>
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function InvestorLeftPanel({
  investor,
}: {
  investor: {
    name: string
    logoUrl: string | null
    websiteUrl: string | null
    description: string | null
    investmentAreas: { value: string; label: string }[]
    investmentGeographies: { value: string; label: string }[]
  }
}) {
  return (
    <div className="hidden w-1/2 flex-col justify-between bg-gradient-to-br from-[#f97316]/10 via-background to-background p-12 lg:flex">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          {investor.logoUrl ? (
            <img
              src={investor.logoUrl}
              alt={investor.name}
              className="size-10 rounded-xl bg-card object-cover"
            />
          ) : (
            <div className="flex size-10 items-center justify-center rounded-xl bg-card">
              <Briefcase className="size-5 text-[#f97316]" />
            </div>
          )}
          <div>
            <h2 className="text-lg font-semibold text-foreground">{investor.name}</h2>
            {investor.websiteUrl && (
              <a
                href={investor.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                <Globe className="size-3" />
                {new URL(investor.websiteUrl).hostname}
              </a>
            )}
          </div>
        </div>
        {investor.description && (
          <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
            {investor.description}
          </p>
        )}
      </div>

      <div className="max-w-md space-y-5">
        {investor.investmentAreas.length > 0 && (
          <div>
            <p className="mb-2.5 text-[11px] font-medium uppercase tracking-widest text-muted-foreground/60">
              Investment Focus
            </p>
            <div className="flex flex-wrap gap-1.5">
              {investor.investmentAreas.map((area) => (
                <span
                  key={area.value}
                  className="rounded-full bg-[#f97316]/10 px-2.5 py-0.5 text-xs font-medium text-[#f97316]"
                >
                  {area.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {investor.investmentGeographies.length > 0 && (
          <div>
            <p className="mb-2.5 text-[11px] font-medium uppercase tracking-widest text-muted-foreground/60">
              Geographies
            </p>
            <div className="flex flex-wrap gap-1.5">
              {investor.investmentGeographies.map((geo) => (
                <span
                  key={geo.value}
                  className="inline-flex items-center gap-1 rounded-full bg-card px-2.5 py-0.5 text-xs text-muted-foreground"
                >
                  <MapPin className="size-2.5" />
                  {geo.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground/80">
        Powered by <span className="font-medium text-muted-foreground">SeedForge</span>
      </p>
    </div>
  )
}

function MobileInvestorHeader({
  investor,
}: {
  investor: { name: string; logoUrl: string | null }
}) {
  return (
    <div className="flex items-center gap-3 lg:hidden">
      {investor.logoUrl ? (
        <img
          src={investor.logoUrl}
          alt={investor.name}
          className="size-10 rounded-lg bg-card object-cover"
        />
      ) : (
        <div className="flex size-10 items-center justify-center rounded-lg bg-card">
          <Briefcase className="size-5 text-[#f97316]" />
        </div>
      )}
      <h2 className="text-lg font-semibold text-foreground">{investor.name}</h2>
    </div>
  )
}
